/**
 * User-facing copy for enforcement rejections (ENFORCEMENT-POLICY.md §6.5, code 6).
 *
 * WHAT THIS IS
 *   A pure function from the server's `enforcement_error` envelope to the words the modal and the
 *   card show. The server's `reason` is shape-prefixed (`ip_family_daily_cpu_limit`,
 *   `primary_daily_limit`, ...) and its `userMessage` carries the row description and the raw
 *   counters ("<row>: ip_family_daily_cpu_limit limit reached (14400/14400)"). None of that is for
 *   a user: the copy below is built from `reason`, `currentTier`, the counters and the reset hint
 *   only, and the server text is never echoed for a 429 envelope.
 *
 * WHY A LEAF MODULE
 *   The service that owns the modal stream pulls inversify and rxjs; the card renders this copy on
 *   every paint and the specs pin every string. No imports, so both can read it for free.
 *
 * COPY RULES (owner, 2026-10-11/12)
 *   - never a pattern name, bucket name or "(n/n)" counter;
 *   - "This connection" for an IP identity, "This account" for a logged-in free user, "Your plan" for
 *     a paid tier — never "account" for an IP;
 *   - a daily limit says WHEN it resets (00:00 UTC and the user's local time), never "try again
 *     later"; `retryAfterSeconds` from the body is the reset distance when present;
 *   - free users get a Log in CTA (anonymous) and a "see plans" link.
 */

export type EnforcementAudience = 'connection' | 'account' | 'paid';

export type EnforcementLimitKind =
    | 'daily_cpu_limit'
    | 'daily_limit'
    | 'concurrent_limit'
    | 'blocked'
    | 'other';

/** The envelope fields this module reads. Structural: the WS frame and the HTTP body both fit. */
export interface EnforcementEnvelope {
    reason?: string;
    currentTier?: string;
    currentValue?: number;
    limitValue?: number;
    upgradeRequired?: boolean;
    suggestedTier?: string;
    /** Seconds until the limit lifts (code 6.2 item 4: seconds to 00:00 UTC for daily limits). */
    retryAfterSeconds?: number;
    /** ISO instant for a `blocked` identity (code 9). */
    blockedUntil?: string;
}

export interface EnforcementCta {
    kind: 'login' | 'plans';
    label: string;
    href: string;
}

export interface EnforcementCopy {
    kind: EnforcementLimitKind;
    audience: EnforcementAudience;
    title: string;
    /** The modal body: one or two sentences, already in the user's words. */
    body: string;
    /** The card's single line: the same fact in one sentence, with "Resets at HH:MM" when known. */
    cardLine: string;
    /** Friendly tier label for the badge, or null when there is nothing worth showing. */
    tierLabel: string | null;
    /** "240 of 240 minutes" — the counters in the unit the body uses. Null when the body has none. */
    usage: { used: string; limit: string; ratio: number } | null;
    /** Epoch ms when the limit lifts, when known. */
    resetAtMs: number | null;
    ctas: EnforcementCta[];
}

export interface EnforcementCopyOptions {
    nowMs?: number;
    /** Local-time formatter for a reset instant; defaults to the browser locale, HH:MM. */
    formatLocalTime?: (ms: number) => string;
    /** Local date formatter for a block end; defaults to the browser locale, date + time. */
    formatLocalDate?: (ms: number) => string;
    loginHref?: string;
    plansHref?: string;
}

export const ENFORCEMENT_PLANS_HREF = '/product';
/** The portal asks for a login when there is none; the modal opens the login dialog itself when it can. */
export const ENFORCEMENT_LOGIN_HREF = '/portal';

const BUCKET_PREFIX = /^(session|ip_family|primary)_/;

/** `ip_family_daily_cpu_limit` -> `daily_cpu_limit`; unknown strings stay unknown. */
export function enforcementKindOf(reason: string | undefined): EnforcementLimitKind {
    if (!reason) return 'other';
    const bare = reason.replace(BUCKET_PREFIX, '');
    switch (bare) {
        case 'daily_cpu_limit':
        case 'daily_limit':
        case 'concurrent_limit':
        case 'blocked':
            return bare;
        default:
            return 'other';
    }
}

/**
 * Who the sentence is about. The tier is the authority; with no tier, an IP-keyed bucket
 * (`ip_family_*` / `session_*`) is a connection and anything else is treated as a connection
 * too — "account" is only ever said when the server says the identity is a user.
 */
export function enforcementAudienceOf(envelope: EnforcementEnvelope): EnforcementAudience {
    const tier = (envelope.currentTier || '').toUpperCase();
    if (tier === 'BASIC' || tier === 'PRO' || tier === 'ENTERPRISE') return 'paid';
    if (tier === 'FREE_REGISTERED') return 'account';
    if (tier === 'FREE_ANONYMOUS') return 'connection';
    if (/^primary_/.test(envelope.reason || '')) return 'account';
    return 'connection';
}

export function enforcementTierLabel(tier: string | undefined): string | null {
    switch ((tier || '').toUpperCase()) {
        case 'FREE_ANONYMOUS': return 'Free · not logged in';
        case 'FREE_REGISTERED': return 'Free account';
        case 'BASIC': return 'Basic';
        case 'PRO': return 'Pro';
        case 'ENTERPRISE': return 'Enterprise';
        default: return null;
    }
}

/** Next 00:00 UTC strictly after `nowMs`. */
export function nextUtcMidnightMs(nowMs: number): number {
    const d = new Date(nowMs);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
}

export function defaultFormatLocalTime(ms: number): string {
    try {
        return new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    } catch {
        const d = new Date(ms);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
}

export function defaultFormatLocalDate(ms: number): string {
    try {
        return new Date(ms).toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return new Date(ms).toISOString();
    }
}

/** 14400 s -> "240"; 27000 s as hours -> "7.5"; never "7.0". */
function trimmed(n: number): string {
    const rounded = Math.round(n * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function minutes(seconds: number): string { return trimmed(Math.round(seconds / 60)); }
function hours(seconds: number): string { return trimmed(seconds / 3600); }

function pluralise(count: string, unit: string): string {
    return count === '1' ? unit : `${unit}s`;
}

function subjectOf(audience: EnforcementAudience): string {
    return audience === 'account' ? 'This account' : 'This connection';
}

function ctasFor(audience: EnforcementAudience, kind: EnforcementLimitKind, opts: Required<Pick<EnforcementCopyOptions, 'loginHref' | 'plansHref'>>): EnforcementCta[] {
    if (kind === 'blocked') return [];
    const ctas: EnforcementCta[] = [];
    if (audience === 'connection') ctas.push({ kind: 'login', label: 'Log in', href: opts.loginHref });
    if (audience !== 'paid' || kind === 'daily_cpu_limit' || kind === 'daily_limit') {
        ctas.push({ kind: 'plans', label: 'See plans', href: opts.plansHref });
    }
    return ctas;
}

function freeTail(audience: EnforcementAudience): string {
    return audience === 'connection'
        ? 'Log in for a larger daily allowance, or see plans.'
        : 'See plans for a larger daily allowance.';
}

/** After a refusal, is pressing Retry meaningful? Not for a daily budget or a block: it is refused again until the reset. */
export function refusalRetryMeaningful(copy: EnforcementCopy | null | undefined): boolean {
    if (!copy) return true;
    return copy.kind !== 'daily_cpu_limit' && copy.kind !== 'daily_limit' && copy.kind !== 'blocked';
}

/**
 * Build every string the UI needs from one envelope. Deterministic given `nowMs` and the
 * formatters, so the specs can pin the exact sentences.
 */
export function enforcementCopy(envelope: EnforcementEnvelope, options: EnforcementCopyOptions = {}): EnforcementCopy {
    const nowMs = options.nowMs ?? Date.now();
    const fmtTime = options.formatLocalTime ?? defaultFormatLocalTime;
    const fmtDate = options.formatLocalDate ?? defaultFormatLocalDate;
    const hrefs = { loginHref: options.loginHref ?? ENFORCEMENT_LOGIN_HREF, plansHref: options.plansHref ?? ENFORCEMENT_PLANS_HREF };

    const kind = enforcementKindOf(envelope.reason);
    const audience = enforcementAudienceOf(envelope);
    const tierLabel = enforcementTierLabel(envelope.currentTier);
    const ctas = ctasFor(audience, kind, hrefs);
    const used = envelope.currentValue;
    const limit = envelope.limitValue;
    const haveCounters = typeof used === 'number' && typeof limit === 'number' && limit > 0;
    const ratio = haveCounters ? Math.min(1, Math.max(0, used! / limit!)) : 0;

    const isDaily = kind === 'daily_cpu_limit' || kind === 'daily_limit';
    const retryAfter = typeof envelope.retryAfterSeconds === 'number' && envelope.retryAfterSeconds >= 0
        ? envelope.retryAfterSeconds : null;
    const resetAtMs = isDaily
        ? (retryAfter !== null ? nowMs + retryAfter * 1000 : nextUtcMidnightMs(nowMs))
        : null;
    const local = resetAtMs !== null ? fmtTime(resetAtMs) : '';
    const resetSentence = `It resets at 00:00 UTC (${local}).`;
    const cardReset = `Resets at ${local}.`;

    switch (kind) {
        case 'daily_cpu_limit': {
            if (audience === 'paid') {
                const u = haveCounters ? `${hours(used!)} of ${hours(limit!)} ${pluralise(hours(limit!), 'hour')}` : '';
                const counters = u ? ` (${u})` : '';
                return {
                    kind, audience, tierLabel, resetAtMs, ctas,
                    title: 'Daily processing time used up',
                    body: `Your plan's daily processing time is used up${counters}; it resets at 00:00 UTC (${local}).`,
                    cardLine: `Your plan's daily processing time is used up${counters}. ${cardReset}`,
                    usage: haveCounters ? { used: `${hours(used!)} h`, limit: `${hours(limit!)} h`, ratio } : null,
                };
            }
            const counters = haveCounters ? ` (${minutes(used!)} of ${minutes(limit!)} minutes)` : '';
            return {
                kind, audience, tierLabel, resetAtMs, ctas,
                title: 'Daily free processing limit reached',
                body: `${subjectOf(audience)} has used today's free video-processing time${counters}. ${resetSentence} ${freeTail(audience)}`,
                cardLine: `Daily free processing limit reached${counters}. ${cardReset}`,
                usage: haveCounters ? { used: `${minutes(used!)} min`, limit: `${minutes(limit!)} min`, ratio } : null,
            };
        }
        case 'daily_limit': {
            const counters = haveCounters ? ` (${trimmed(used!)} of ${trimmed(limit!)} conversions)` : '';
            if (audience === 'paid') {
                return {
                    kind, audience, tierLabel, resetAtMs, ctas,
                    title: 'Daily conversion limit reached',
                    body: `Your plan's daily video conversions are used up${counters}; they reset at 00:00 UTC (${local}).`,
                    cardLine: `Your plan's daily video conversions are used up${counters}. ${cardReset}`,
                    usage: haveCounters ? { used: trimmed(used!), limit: trimmed(limit!), ratio } : null,
                };
            }
            return {
                kind, audience, tierLabel, resetAtMs, ctas,
                title: 'Daily free conversion limit reached',
                body: `${subjectOf(audience)} has used today's free video conversions${counters}. ${resetSentence} ${freeTail(audience)}`,
                cardLine: `Daily free conversion limit reached${counters}. ${cardReset}`,
                usage: haveCounters ? { used: trimmed(used!), limit: trimmed(limit!), ratio } : null,
            };
        }
        case 'concurrent_limit': {
            const n = typeof limit === 'number' && limit > 0 ? trimmed(limit) : null;
            const running = n ? `${n} ${pluralise(n, 'conversion')}` : 'its conversions';
            const who = audience === 'paid' ? 'Your plan' : subjectOf(audience);
            const verb = audience === 'paid' ? 'runs up to' : 'already has';
            return {
                kind, audience, tierLabel, resetAtMs, ctas,
                title: 'Too many conversions at once',
                body: `${who} ${verb} ${running} running at once. The next one starts when one finishes.`,
                cardLine: `Too many conversions at once${n ? ` (${n} running)` : ''}. Starts when one finishes.`,
                usage: null,
            };
        }
        case 'blocked': {
            // The envelope carries `retryAfterSeconds` (seconds until blockedUntil) rather than the
            // instant itself; an open-ended block sends neither and stays undated.
            const untilMs = envelope.blockedUntil
                ? Date.parse(envelope.blockedUntil)
                : (retryAfter !== null && retryAfter > 0 ? nowMs + retryAfter * 1000 : NaN);
            const until = Number.isFinite(untilMs) ? ` until ${fmtDate(untilMs)}` : '';
            const who = audience === 'connection' ? 'This connection' : 'This account';
            const line = `${who} is blocked${until}. Contact support.`;
            return {
                kind, audience, tierLabel, ctas,
                resetAtMs: Number.isFinite(untilMs) ? untilMs : null,
                title: 'Access blocked',
                body: line,
                cardLine: line,
                usage: null,
            };
        }
        default:
            return {
                kind, audience, tierLabel, resetAtMs: null, ctas,
                title: "You've hit a usage limit",
                body: audience === 'paid'
                    ? 'This request was refused by a usage limit on your plan. Try again later.'
                    : `This request was refused by a usage limit. ${audience === 'connection' ? 'Log in for a larger allowance, or see plans.' : 'See plans for a larger allowance.'}`,
                cardLine: 'Usage limit reached. Try again later.',
                usage: null,
            };
    }
}
